import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule, Provider, forwardRef } from '@angular/core';
import { ErrorScreenComponent } from './shared/error-screen/error-screen.component';
import { GlobalErrorHandler } from './shared/global-error-handler';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';

import { AngularFireMessagingModule } from '@angular/fire/messaging';
import { AngularFireDatabaseModule } from '@angular/fire/database';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire';
import { MessagingService } from './services/messaging.service';
import { environment } from '../environments/environment';
import { AsyncPipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { ChartsModule } from 'ng2-charts';

import { AppComponent } from './app.component';

// Import containers
import { DefaultLayoutComponent } from './containers';
import { SidebarComponent } from './containers/default-layout/sidebar/sidebar.component';
import { SharedUiModule } from './shared/ui/shared-ui.module';

import { P404Component } from './views/error/404.component';
import { P500Component } from './views/error/500.component';
import { LoginComponent } from './views/login/login.component';

const APP_CONTAINERS = [
  DefaultLayoutComponent,
  SidebarComponent
];

// Import routing module
import { AppRoutingModule } from './app.routing';

// Import 3rd party components

import { AngularEditorModule } from '@kolkov/angular-editor';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { ApiInterceptor } from './common/service';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { LoaderModule } from './shared/loader/loader.module';
import { ForgetPasswordComponent } from './views/login/forgetPassword.component';
import { ResetPasswordComponent } from './views/login/resetPassword.component';
import { SignUpComponent } from './views/login/signup.component';
import { PrivacyPolicyComponent } from './views/login/privacyPolicy.component';
import { TermsConditionsComponent } from './views/login/termsConditions.component';
import { AddSelfGuestComponent } from './views/resident/addSelfGuest.component';
import { AddSelfCheckInComponent } from './views/resident/addSelfCheckIn.component';
import { SelfCheckInFormComponent } from './views/SelfCheckIn/selfCheckInForm.component';
import { SuccessComponent } from './views/error/success.component';
import { ExpireComponent } from './views/error/expire.component';
import { vehicleModule } from './views/vehicleManagement/vehicle.module';
import {scrapAgentModule } from './views/ScrapAgentManagement/scrapAgent.module';

export const API_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiInterceptor),
  multi: true
  };

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ChartsModule,
    BsDropdownModule.forRoot(),
    HttpClientModule,
    ReactiveFormsModule,
    ModalModule.forRoot(),
    TimepickerModule.forRoot(),
    AngularEditorModule,
    AngularFireDatabaseModule,
    AngularFireAuthModule,
    AngularFireMessagingModule,
    AutocompleteLibModule,
    LoaderModule,
    SharedUiModule,
    FormsModule,
    vehicleModule,
    scrapAgentModule,
    AngularFireModule.initializeApp(environment.firebase),
    ToastrModule.forRoot({
      timeOut: 4000,
      preventDuplicates: true,
      // Default 'toast-top-right' sits directly under the fixed header's
      // icon buttons — bottom-right keeps toasts from covering live chrome.
      positionClass: 'toast-bottom-right',
      // ngx-toastr drives its own fly-in/out via inline styles (not plain
      // CSS we could override from a stylesheet) — easing/easeTime are its
      // own config hooks for that, swapped from the library default
      // ('ease-in'/300ms, a flat linear-feeling snap) to match the settle
      // curve used everywhere else in the app.
      easing: 'cubic-bezier(.22, 1, .36, 1)',
      easeTime: 280,
      toastClass: 'ngx-toastr vw-toast',
    })
  ],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    P404Component,
    P500Component,
    SuccessComponent,
    ExpireComponent,
    LoginComponent,
    ForgetPasswordComponent,
    ResetPasswordComponent,
    AddSelfGuestComponent,
    AddSelfCheckInComponent,
    SelfCheckInFormComponent,
    SignUpComponent,
    PrivacyPolicyComponent,
    TermsConditionsComponent,
    ErrorScreenComponent
    ],
  providers: [
    ApiInterceptor,
    MessagingService,
    AsyncPipe,
    API_INTERCEPTOR_PROVIDER,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
    provide: LocationStrategy,
    useClass: HashLocationStrategy,
  }],
  bootstrap: [ AppComponent ]
})
export class AppModule { }
